"use client";

import { useRef, useState, type KeyboardEvent } from "react";
import type { ComparisonRow } from "@/content/landing";
import { cn } from "@/lib/utils";

type Mode = "b2b" | "b2c";

type B2bB2cCompareProps = {
  headline: string;
  rows: ComparisonRow[];
};

export function B2bB2cCompare({ headline, rows }: B2bB2cCompareProps) {
  const [mode, setMode] = useState<Mode>("b2b");
  const groupRef = useRef<HTMLDivElement>(null);

  const modes: Mode[] = ["b2b", "b2c"];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      setMode((current) => (current === "b2b" ? "b2c" : "b2b"));
      const next = modes.find((item) => item !== mode);
      if (next) groupRef.current?.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)?.focus();
    }
  };

  const renderSwitch = () => (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Srovnání režimů B2B a B2C"
      onKeyDown={onKeyDown}
      className="inline-flex rounded-full border border-paper-line bg-paper-card p-1"
    >
      {modes.map((item) => {
        const selected = mode === item;
        return (
          <button
            key={item}
            type="button"
            role="radio"
            aria-checked={selected}
            data-mode={item}
            tabIndex={selected ? 0 : -1}
            onClick={() => setMode(item)}
            className={cn(
              "rounded-full px-5 py-2 text-[14px] font-medium transition-colors duration-[var(--duration-fast)] active:scale-[0.99]",
              selected
                ? item === "b2b"
                  ? "bg-signal text-ink-700"
                  : "bg-blue text-white"
                : "text-paper-muted hover:text-paper-text",
            )}
          >
            {item.toUpperCase()}
          </button>
        );
      })}
    </div>
  );

  const renderValue = (row: ComparisonRow, key: Mode) => (
    <span className={cn("block transition-opacity duration-[var(--duration-base)]", mode === key ? "opacity-100" : "opacity-45")}>
      {row[key]}
    </span>
  );

  return (
    <section id="srovnani" aria-labelledby="srovnani-title" className="paper-scope section-pad">
      <div className="container-page">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <h2 id="srovnani-title" className="h2-display max-w-[560px]">
            {headline}
          </h2>
          {renderSwitch()}
        </div>

        {/* Desktop table */}
        <div className="mt-12 hidden md:block">
          <div className="grid grid-cols-[1.3fr_1fr_1fr] border-t border-paper-line">
            <div className="py-4 pr-6">
              <span className="mono-label text-paper-muted">HLEDISKO</span>
            </div>
            <div className="border-l-2 border-signal bg-wash px-6 py-4">
              <span className="mono-label text-blue">B2B</span>
            </div>
            <div className="px-6 py-4">
              <span className="mono-label text-paper-muted">B2C</span>
            </div>
          </div>

          {rows.map((row) => (
            <div
              key={row.aspect}
              className="grid grid-cols-[1.3fr_1fr_1fr] border-t border-paper-line"
            >
              <div className="py-5 pr-6 text-[15px] leading-relaxed text-paper-text">
                {row.aspect}
              </div>
              <div className="border-l-2 border-signal bg-wash px-6 py-5 text-[15px] leading-relaxed text-paper-text">
                {renderValue(row, "b2b")}
              </div>
              <div className="px-6 py-5 text-[15px] leading-relaxed text-paper-text">
                {renderValue(row, "b2c")}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile stacked cards */}
        <div className="mt-10 flex flex-col gap-5 md:hidden">
          {[...modes]
            .sort((a, b) => (a === mode ? -1 : b === mode ? 1 : 0))
            .map((item) => (
              <article
                key={item}
                className={cn(
                  "rounded-[10px] border bg-paper-card p-5 transition-opacity duration-[var(--duration-base)]",
                  item === "b2b" ? "border-l-2 border-signal border-paper-line" : "border-paper-line",
                  mode === item ? "opacity-100" : "opacity-45",
                )}
              >
                <h3 className="mono-index mb-4 text-blue">{item.toUpperCase()}</h3>
                <dl className="space-y-4">
                  {rows.map((row) => (
                    <div key={row.aspect}>
                      <dt className="text-[13px] font-medium text-paper-muted">{row.aspect}</dt>
                      <dd className="mt-1 text-[15px] leading-relaxed text-paper-text">
                        {row[item]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
        </div>

        <p aria-live="polite" className="sr-only">
          Zvýrazněn režim {mode.toUpperCase()}.
        </p>
      </div>
    </section>
  );
}
